<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Settings</h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Settings</span>
        </div>
      </div>
    </div>

    <!-- Notice banner -->
    <div class="gl-notice-banner">
      <div class="gl-notice-icon">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <p class="gl-notice-text">
        Loyalty rules (points earning, redemption rates, tier thresholds) are managed by the platform.
        <router-link to="/owner/loyalty" class="gl-notice-link">View how loyalty works →</router-link>
      </p>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="gl-settings-card">
      <div class="gl-skel-tabs">
        <div class="gl-skel gl-skel-pill"></div>
        <div class="gl-skel gl-skel-pill"></div>
      </div>
      <div class="gl-skel-rows">
        <div v-for="i in 3" :key="i" class="gl-skel-row-item">
          <div>
            <div class="gl-skel gl-skel-line" style="width:160px;margin-bottom:8px"></div>
            <div class="gl-skel gl-skel-line" style="width:260px;height:11px"></div>
          </div>
          <div class="gl-skel gl-skel-input"></div>
        </div>
      </div>
    </div>

    <!-- Settings card -->
    <div v-else class="gl-settings-card">
      <!-- Tabs -->
      <div class="gl-tab-bar gl-tab-bar-inset">
        <button
          v-for="tab in tabs" :key="tab.key"
          class="gl-tab"
          :class="{ 'gl-tab-active': activeTab === tab.key }"
          @click="activeTab = tab.key"
          type="button"
        >
          <span class="gl-tab-icon" v-html="tab.svg"></span>
          {{ tab.label }}
        </button>
      </div>

      <!-- Empty state -->
      <div v-if="!activeGroup || !activeGroup.length" class="gl-empty-settings">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
        <span>No settings in this group.</span>
      </div>

      <!-- Settings form -->
      <form v-else @submit.prevent="save">
        <div class="gl-settings-body">
          <div
            v-for="(setting, idx) in activeGroup"
            :key="setting.key"
            class="gl-setting-row"
            :class="{ 'gl-setting-row-last': idx === activeGroup.length - 1 }"
          >
            <!-- Label + description -->
            <div class="gl-setting-label-col">
              <div class="gl-setting-label">{{ formatKey(setting.key) }}</div>
              <div v-if="setting.description" class="gl-setting-desc">{{ setting.description }}</div>
              <div v-else class="gl-setting-desc">{{ settingHint(setting.key) }}</div>
            </div>

            <!-- Control -->
            <div class="gl-setting-control-col">
              <!-- Boolean toggle -->
              <template v-if="setting.type === 'boolean'">
                <button
                  type="button"
                  class="gl-toggle"
                  :class="{ 'gl-toggle-on': form[setting.key] }"
                  @click="form[setting.key] = !form[setting.key]"
                  :aria-checked="!!form[setting.key]"
                  role="switch"
                >
                  <span class="gl-toggle-thumb"></span>
                </button>
                <span class="gl-toggle-label">{{ form[setting.key] ? 'Enabled' : 'Disabled' }}</span>
              </template>

              <!-- Integer / number with optional unit suffix -->
              <template v-else-if="setting.type === 'integer'">
                <div class="gl-input-unit-wrap">
                  <input
                    type="number"
                    class="gl-input gl-input-num"
                    v-model.number="form[setting.key]"
                    step="1"
                    min="0"
                  >
                  <span v-if="unitFor(setting.key)" class="gl-unit-suffix">{{ unitFor(setting.key) }}</span>
                </div>
              </template>

              <!-- Decimal / float -->
              <template v-else-if="setting.type === 'decimal'">
                <div class="gl-input-unit-wrap">
                  <input
                    type="number"
                    class="gl-input gl-input-num"
                    v-model.number="form[setting.key]"
                    step="0.01"
                    min="0"
                  >
                  <span v-if="unitFor(setting.key)" class="gl-unit-suffix">{{ unitFor(setting.key) }}</span>
                </div>
              </template>

              <!-- JSON textarea -->
              <template v-else-if="setting.type === 'json'">
                <div class="gl-json-wrap">
                  <textarea
                    class="gl-textarea gl-textarea-mono"
                    v-model="jsonStrings[setting.key]"
                    rows="5"
                    :class="{ 'gl-input-error': jsonErrors[setting.key] }"
                    spellcheck="false"
                  ></textarea>
                  <div v-if="jsonErrors[setting.key]" class="gl-error-msg">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {{ jsonErrors[setting.key] }}
                  </div>
                </div>
              </template>

              <!-- Text -->
              <template v-else>
                <input type="text" class="gl-input" v-model="form[setting.key]">
              </template>
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="gl-settings-footer">
          <button type="button" class="gl-btn-ghost" @click="resetForm" :disabled="saving">Reset</button>
          <button type="submit" class="gl-btn-save" :disabled="saving">
            <span v-if="saving" class="gl-spinner gl-spinner-white"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Save Changes
          </button>
        </div>
      </form>
    </div>

    <!-- Success Toast -->
    <transition name="gl-toast">
      <div v-if="toastMsg" class="gl-toast" :class="toastType === 'error' ? 'gl-toast-error' : 'gl-toast-success'">
        <svg v-if="toastType !== 'error'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        {{ toastMsg }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ownerSettingsApi } from '@/services/ownerApi';

const TAB_SVG = {
  general:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>',
  delivery: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
};

const UNIT_MAP = {
  tax_rate: '%',
  service_charge: '%',
  delivery_fee: 'RM',
  minimum_order: 'RM',
  free_delivery_threshold: 'RM',
  max_delivery_radius: 'km',
};

const HINT_MAP = {
  tax_rate: 'Applied to all order subtotals',
  service_charge: 'Service charge added to orders',
  delivery_fee: 'Base delivery fee charged to customers',
  minimum_order: 'Minimum order amount to place an order',
  free_delivery_threshold: 'Orders above this amount get free delivery',
  max_delivery_radius: 'Maximum delivery radius from branch',
};

const TAB_CONFIG = [
  { key: 'general',  label: 'General',  svg: TAB_SVG.general },
  { key: 'delivery', label: 'Delivery', svg: TAB_SVG.delivery },
];

export default {
  name: 'OwnerSettings',
  data() {
    return {
      loading: true,
      saving: false,
      grouped: {},
      form: {},
      jsonStrings: {},
      jsonErrors: {},
      activeTab: 'general',
      toastMsg: null,
      toastType: 'success',
      toastTimer: null,
    };
  },
  computed: {
    tabs() {
      const groupKeys = Object.keys(this.grouped);
      const known = TAB_CONFIG.filter(t => groupKeys.includes(t.key));
      const extras = groupKeys
        .filter(k => !TAB_CONFIG.find(t => t.key === k))
        .map(k => ({ key: k, label: this.capitalize(k), svg: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/></svg>' }));
      return [...known, ...extras];
    },
    activeGroup() {
      return this.grouped[this.activeTab] || [];
    }
  },
  mounted() {
    this.fetchSettings();
  },
  methods: {
    async fetchSettings() {
      try {
        this.loading = true;
        const response = await ownerSettingsApi.get();
        this.grouped = response.data.data || {};
        this.hydrateForm();
        if (!this.grouped[this.activeTab] && this.tabs.length) {
          this.activeTab = this.tabs[0].key;
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        this.loading = false;
      }
    },
    hydrateForm() {
      const form = {};
      const jsonStrings = {};
      Object.values(this.grouped).flat().forEach(s => {
        if (s.type === 'json') {
          jsonStrings[s.key] = s.value != null ? JSON.stringify(s.value, null, 2) : '';
        } else {
          form[s.key] = s.value;
        }
      });
      this.form = form;
      this.jsonStrings = jsonStrings;
      this.jsonErrors = {};
    },
    resetForm() {
      this.hydrateForm();
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    capitalize(s) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    unitFor(key) {
      return UNIT_MAP[key] || null;
    },
    settingHint(key) {
      return HINT_MAP[key] || null;
    },
    showToast(msg, type = 'success') {
      this.toastMsg = msg;
      this.toastType = type;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastMsg = null; }, 3500);
    },
    async save() {
      this.jsonErrors = {};
      const payload = [];

      for (const setting of Object.values(this.grouped).flat()) {
        if (setting.type === 'json') {
          const raw = this.jsonStrings[setting.key];
          if (raw && raw.trim()) {
            try {
              payload.push({ key: setting.key, value: JSON.parse(raw) });
            } catch {
              this.jsonErrors[setting.key] = 'Invalid JSON';
            }
          } else {
            payload.push({ key: setting.key, value: null });
          }
        } else {
          payload.push({ key: setting.key, value: this.form[setting.key] });
        }
      }

      if (Object.keys(this.jsonErrors).length) return;

      try {
        this.saving = true;
        const response = await ownerSettingsApi.update(payload);
        this.grouped = response.data.data || {};
        this.hydrateForm();
        this.showToast('Settings saved successfully.');
      } catch (error) {
        this.showToast(error.response?.data?.message || 'Failed to save settings.', 'error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 28px 32px; max-width: 900px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Notice banner ── */
.gl-notice-banner {
  display: flex; align-items: flex-start; gap: 10px;
  background: #fffceb; border: 1px solid #f0e088; border-radius: 10px;
  padding: 13px 16px; margin-bottom: 24px;
}
.gl-notice-icon { color: #b8920a; margin-top: 1px; flex-shrink: 0; }
.gl-notice-text { font-size: 13px; color: #7a6520; margin: 0; line-height: 1.5; }
.gl-notice-link { color: #900C27; font-weight: 600; text-decoration: none; }
.gl-notice-link:hover { text-decoration: underline; }

/* ── Settings card ── */
.gl-settings-card {
  background: #fff; border: 1px solid #eaedec; border-radius: 14px;
  overflow: hidden;
}

/* ── Tabs ── */
.gl-tab-bar { display: flex; gap: 2px; border-bottom: 2px solid #eef0ef; padding: 0 24px; }
.gl-tab-bar-inset { padding-left: 24px; padding-right: 24px; }
.gl-tab { display: flex; align-items: center; gap: 7px; padding: 14px 16px; font-size: 13.5px; font-weight: 500; color: #6b7f7b; background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; cursor: pointer; transition: color .15s, border-color .15s; }
.gl-tab:hover { color: #900C27; }
.gl-tab-active { color: #900C27; border-bottom-color: #900C27; font-weight: 600; }
.gl-tab-icon { display: flex; align-items: center; }

/* ── Settings body ── */
.gl-settings-body { padding: 8px 0; }

.gl-setting-row {
  display: grid; grid-template-columns: 1fr 340px;
  gap: 32px; align-items: center;
  padding: 20px 28px; border-bottom: 1px solid #f3f5f4;
  transition: background .12s;
}
.gl-setting-row:hover { background: #fbfcfb; }
.gl-setting-row-last { border-bottom: none; }

.gl-setting-label-col { min-width: 0; }
.gl-setting-label { font-size: 14px; font-weight: 600; color: #1a2e2b; margin-bottom: 3px; }
.gl-setting-desc { font-size: 12.5px; color: #8a9e9a; line-height: 1.4; }

.gl-setting-control-col { display: flex; align-items: center; gap: 10px; }

/* ── Inputs ── */
.gl-input {
  width: 100%; padding: 9px 13px; font-size: 13.5px; color: #1a2e2b;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px;
  outline: none; transition: border-color .15s, background .15s;
  font-family: inherit; box-sizing: border-box;
}
.gl-input:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,.08); }
.gl-input-num { width: 120px; text-align: right; }
.gl-input-error { border-color: #ef4444 !important; background: #fff8f8 !important; }

.gl-textarea {
  width: 100%; padding: 10px 13px; font-size: 13px; color: #1a2e2b;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px;
  outline: none; transition: border-color .15s; resize: vertical;
  font-family: inherit; box-sizing: border-box;
}
.gl-textarea:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,.08); }
.gl-textarea-mono { font-family: 'Menlo', 'Consolas', monospace; font-size: 12px; }

.gl-input-unit-wrap { display: flex; align-items: center; gap: 0; }
.gl-unit-suffix {
  padding: 9px 12px; font-size: 13px; font-weight: 600; color: #5a7470;
  background: #eef3f2; border: 1.5px solid #e0e9e7; border-left: none;
  border-radius: 0 8px 8px 0; white-space: nowrap;
}
.gl-input-unit-wrap .gl-input { border-radius: 8px 0 0 8px; width: 100px; }

.gl-json-wrap { width: 100%; }
.gl-error-msg { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #dc2626; margin-top: 5px; }

/* ── Toggle switch ── */
.gl-toggle {
  width: 44px; height: 24px; border-radius: 12px; flex-shrink: 0;
  background: #d1d9d7; border: none; cursor: pointer; position: relative;
  transition: background .2s; padding: 0;
}
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.2);
  transition: transform .2s;
}
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }
.gl-toggle-label { font-size: 13px; color: #5a7470; font-weight: 500; min-width: 55px; }

/* ── Footer ── */
.gl-settings-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 18px 28px; border-top: 1.5px solid #eef0ef; background: #f9fbfa;
}
.gl-btn-save {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 22px; font-size: 13.5px; font-weight: 600;
  background: #900C27; color: #fff; border: none; border-radius: 8px;
  cursor: pointer; transition: background .15s;
}
.gl-btn-save:hover:not(:disabled) { background: #1a5248; }
.gl-btn-save:disabled { opacity: .55; cursor: not-allowed; }
.gl-btn-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 18px; font-size: 13px; font-weight: 500;
  background: #fff; color: #4a6360; border: 1.5px solid #d4dedd;
  border-radius: 8px; cursor: pointer; transition: border-color .15s, background .15s;
}
.gl-btn-ghost:hover:not(:disabled) { border-color: #b0c4c0; background: #f8faf9; }
.gl-btn-ghost:disabled { opacity: .5; cursor: not-allowed; }

/* ── Empty ── */
.gl-empty-settings { display: flex; align-items: center; gap: 10px; color: #a0b5b0; font-size: 13px; padding: 48px 28px; }

/* ── Spinner ── */
.gl-spinner { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .65s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Skeleton ── */
.gl-skel-tabs { display: flex; gap: 8px; padding: 16px 24px; border-bottom: 2px solid #eef0ef; }
.gl-skel-rows { padding: 8px 0; }
.gl-skel-row-item { display: flex; align-items: center; justify-content: space-between; padding: 20px 28px; border-bottom: 1px solid #f3f5f4; }
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-line { height: 13px; }
.gl-skel-pill { height: 32px; width: 80px; border-radius: 8px; }
.gl-skel-input { height: 38px; width: 120px; border-radius: 8px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Toast ── */
.gl-toast { position: fixed; bottom: 28px; right: 28px; z-index: 2000; display: flex; align-items: center; gap: 9px; padding: 12px 18px; border-radius: 10px; font-size: 13.5px; font-weight: 500; box-shadow: 0 8px 24px rgba(0,0,0,.14); }
.gl-toast-success { background: #1a5248; color: #fff; }
.gl-toast-error { background: #dc2626; color: #fff; }
.gl-toast-enter-active, .gl-toast-leave-active { transition: all .3s ease; }
.gl-toast-enter-from, .gl-toast-leave-to { opacity: 0; transform: translateY(12px) scale(.95); }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-page-header { margin-bottom: 14px; }

  /* Notice banner */
  .gl-notice-banner { padding: 11px 13px; margin-bottom: 16px; }
  .gl-notice-text { font-size: 12.5px; }

  /* Tab bar */
  .gl-tab-bar { overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; flex-wrap: nowrap; padding: 0 16px; }
  .gl-tab-bar::-webkit-scrollbar { display: none; }
  .gl-tab-bar-inset { padding-left: 16px; padding-right: 16px; }
  .gl-tab { flex-shrink: 0; padding: 12px 14px; font-size: 13px; white-space: nowrap; }

  /* Setting row: stack label above control */
  .gl-setting-row {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
  }
  .gl-setting-label { font-size: 13.5px; }
  .gl-setting-control-col { justify-content: flex-start; }

  /* Make text inputs fill the row on mobile */
  .gl-setting-control-col > .gl-input { width: 100%; }
  .gl-json-wrap { width: 100%; }

  /* Footer: side-by-side but Save takes more space */
  .gl-settings-footer { padding: 14px 16px; gap: 8px; }
  .gl-btn-save { flex: 1; justify-content: center; }
  .gl-btn-ghost { justify-content: center; }

  /* Skeleton */
  .gl-skel-tabs { padding: 14px 16px; }
  .gl-skel-row-item { flex-direction: column; align-items: flex-start; gap: 12px; padding: 16px; }
  .gl-skel-input { width: 100%; }

  /* Toast: full width */
  .gl-toast { bottom: 16px; right: 16px; left: 16px; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }

body.dark-only .gl-notice-banner { background: #2a2710; border-color: #4a3f10; }
body.dark-only .gl-notice-text { color: #c9a83c; }
body.dark-only .gl-notice-link { color: #4caf9a; }

body.dark-only .gl-settings-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-tab-bar { border-bottom-color: #2a3432; }
body.dark-only .gl-tab { color: #5a7470; }
body.dark-only .gl-tab:hover { color: #4caf9a; }
body.dark-only .gl-tab-active { color: #4caf9a; border-bottom-color: #4caf9a; }

body.dark-only .gl-setting-row { border-bottom-color: #243432; }
body.dark-only .gl-setting-row:hover { background: #1c2f2b; }
body.dark-only .gl-setting-label { color: #e0eded; }
body.dark-only .gl-setting-desc { color: #5a7470; }

body.dark-only .gl-input, body.dark-only .gl-textarea { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-input:focus, body.dark-only .gl-textarea:focus { border-color: #4caf9a; background: #1e2d2b; }
body.dark-only .gl-unit-suffix { background: #1c2f2b; border-color: #2a3d3a; color: #7aada5; }

body.dark-only .gl-toggle { background: #3a4f4c; }
body.dark-only .gl-toggle-on { background: #900C27; }
body.dark-only .gl-toggle-label { color: #7aada5; }

body.dark-only .gl-settings-footer { background: #192b28; border-top-color: #2a3d3a; }
body.dark-only .gl-btn-ghost { background: #1e2d2b; border-color: #2a3d3a; color: #8ab5ad; }
body.dark-only .gl-btn-ghost:hover:not(:disabled) { background: #243432; }

body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-skel-row-item { border-bottom-color: #243432; }
body.dark-only .gl-empty-settings { color: #3d5450; }
</style>
